import * as React from 'react'

import Grid                       from '@material-ui/core/Grid/Grid'
import IconButton                 from '@material-ui/core/IconButton'
import Paper                      from '@material-ui/core/Paper/Paper'
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles'
import CancelIcon                 from '@material-ui/icons/Cancel'
import EditIcon                   from '@material-ui/icons/Edit'
import SaveIcon                   from '@material-ui/icons/Save'

import { AccountType }                                   from '../../api'
import { getStudent, getTeacher }                        from '../../project-utils'
import { isTeacher }                                     from '../../project-utils/permissions'
import { deepOverride }                                  from '../../utils'
import { LabelledText }                                  from '../atoms'
import { StudentWording, TeacherWording }                from '../dynamic-project-wording'
import withAccountType, { InjectedWithAccountTypeProps } from '../hocs/withAccountType'
import withToggle, { InjectedToggleProps }               from '../hocs/withToggle'
import { ValidateTypeField }                             from './index'

interface CoupleProps {
  teacher: string
  student: string
  platform: 'mentoring' | 'coaching'
  onRemove: () => void
  onSave: (couple: { teacher: string, student: string }) => void
  automaticMatch: boolean
}

const styles = theme => ({
  root:                   {
    ...theme.mixins.gutters(),
    paddingTop:    theme.spacing.unit,
    paddingBottom: theme.spacing.unit,
  },
  oneDimensionalExpander: {
    flex: 1,
  },
})

type Props =
  CoupleProps
  & WithStyles<ReturnType<typeof styles>>
  & InjectedToggleProps
  & InjectedWithAccountTypeProps

interface State {
  value: {
    teacher: string
    student: string
  }

  valid: {
    teacher: boolean
    student: boolean
  }
}

class Couple extends React.Component<Props, State> {
  public state = {
    value: {
      teacher: this.props.teacher,
      student: this.props.student,
    },

    valid: {
      teacher: true,
      student: true,
    }
  }

  public render () {
    const { classes } = this.props

    return (
      <Paper className={classes.root}>
        <Grid
          container
          spacing={16}
          justify={'space-between'}
          alignItems={'center'}
        >
          {this.renderContent()}
          {this.renderControls()}
        </Grid>
      </Paper>
    )
  }

  private renderContent = () => {
    const { isActive } = this.props

    return isActive
      ? this.renderEditing()
      : this.renderIdle()
  }

  private renderIdle = () => {
    const {
      teacher,
      student,
    } = this.props

    const teacherWording = <TeacherWording />
    const studentWording = <StudentWording />

    return (
      <Grid item xs={8}>
        <Grid container spacing={16}>
          <Grid item xs={12} sm={6}>
            <LabelledText label={teacherWording}>
              {teacher}
            </LabelledText>
          </Grid>
          <Grid item xs={12} sm={6}>
            <LabelledText label={studentWording}>
              {student}
            </LabelledText>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  private renderEditing = () => {
    const { classes, accountType } = this.props

    const teacherWording = getTeacher(accountType as AccountType)
    const studentWording = getStudent(accountType as AccountType)

    return (
      <>
        <Grid item className={classes.oneDimensionalExpander}>
          <ValidateTypeField
            value={this.state.value.teacher}
            onInvalidation={this.handleInvalidation}
            onValidation={this.handleValidation}
            onChange={this.handleChange}
            accountType={AccountType.Mentor}
            name={'teacher'}
            label={teacherWording}
            fullWidth
            noValidate={this.props.automaticMatch}
          />
        </Grid>
        <Grid item className={classes.oneDimensionalExpander}>
          <ValidateTypeField
            value={this.state.value.student}
            onInvalidation={this.handleInvalidation}
            onValidation={this.handleValidation}
            onChange={this.handleChange}
            accountType={AccountType.Mentee}
            name={'student'}
            label={studentWording}
            fullWidth
            noValidate={this.props.automaticMatch}
          />
        </Grid>
      </>
    )
  }

  private renderControls = () => {
    const {
      isActive,
      deactivate,
      activate
    } = this.props

    const { valid } = this.state

    return isActive
      ? (
        <Grid item xs={12} sm={'auto'}>
          <Grid container justify={'flex-end'} spacing={16}>
            <IconButton
              disabled={!valid.teacher || !valid.student}
              color={'secondary'}
              onClick={this.saveEdit}
            >
              <SaveIcon />
            </IconButton>
            <IconButton
              onClick={deactivate}
              color={'secondary'}
            >
              <CancelIcon />
            </IconButton>
          </Grid>
        </Grid>
      )
      : (
        <Grid item>
          <IconButton
            onClick={activate}
            color={'secondary'}
          >
            <EditIcon />
          </IconButton>
        </Grid>
      )
  }

  private handleChange = ({ target: { name, value } }) => {
    this.setState(state => deepOverride(
      state,
      { value: { [ name ]: value } },
    ))
  }

  private handleValidation = accountType => {
    this.setState(
      state =>
        isTeacher(accountType)
          ?
          deepOverride(
            state,
            {
              valid: { teacher: true },
            }
          )
          :
          deepOverride(
            state,
            {
              valid: { student: true },
            }
          )
    )
  }

  private handleInvalidation = accountType => {
    this.setState(
      state =>
        isTeacher(accountType)
          ?
          deepOverride(
            state,
            {
              valid: { teacher: false },
            }
          )
          :
          deepOverride(
            state,
            {
              valid: { student: false },
            }
          )
    )
  }

  private saveEdit = () => {
    const { value: { teacher, student } } = this.state

    this.props.onSave({ teacher, student })

    this.props.deactivate()
  }
}

export default withStyles(styles)<CoupleProps>(withToggle(withAccountType(Couple)))



// WEBPACK FOOTER //
// ./src/components/molecules/Couple.tsx